import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useRef, useState } from "react";
import type { ExposedRoutesField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  displayStepConfig,
  isConnectorRouteAuth,
  isRouteType,
  type RouteType,
  stepDirFromPath,
} from "../../../shared/stepConfig";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useNavigateOnBranchChange } from "../../hooks/useNavigateOnBranchChange";
import { useQuery } from "../../hooks/useQuery";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import { clientSendContext } from "../../lib/clientSendContext";
import { ROUTE_TYPE_ICONS, routeTypeLabel } from "../../lib/routeTypeDisplay";
import { getPageUrl } from "../../lib/stepUrl";
import { publicDomain, publicUrl } from "../../lib/tenantOrigin";
import { routes as urlRoutes, useCurrentUrl } from "../../lib/urls";
import { writeToClipboard } from "../../lib/writeToClipboard";
import { Box } from "../Box";
import { AnimatedCopyIcon } from "../AnimatedCopyIcon";
import { ClickButton } from "../ClickButton";
import { closeModal } from "../globalModalStack";
import { Icon } from "../Icon";
import { IconBadge } from "../IconBadge";
import { PressButton } from "../PressButton";
import { SoftButton } from "../SoftButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { App } from "./Form";
import { StepInfoContainer, StepList } from "./usageList";

type Props = {
  item: ExposedRoutesField;
  app: App;
};

const SET_UP_ROUTES_PROMPT =
  'I just exposed this workflow as a connector so other workflows can call it. For each link that should be callable through this connector, set `route_auth = "connector"` and `route_type = "api"` in its config.toml, and add an `api.json` contract describing its request and response. Don’t expose internal or helper links. If there’s no link to expose yet, tell me what it should be.';

const RouteRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;

  & + & {
    border-top: 0.5px solid var(--border-alpha);
  }
`;

const EmptyRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 8px;
`;

const AskLink = styled(ClickButton)`
  flex-shrink: 0;
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const CopyButton = styled(PressButton)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

type RouteEntry = {
  branchId: string;
  branchName: string;
  stepDir: string;
  route: string;
  routeType: RouteType;
};

function CopyLinkButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  return (
    <Tooltip content="Copy link">
      <CopyButton
        aria-label="Copy link"
        data-copied={copied || undefined}
        onPress={() => {
          void writeToClipboard(text);
          setCopied(true);
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => setCopied(false), 1500);
        }}
      >
        <AnimatedCopyIcon copied={copied} />
      </CopyButton>
    </Tooltip>
  );
}

export function ConnectorRoutesItem({ item, app }: Props) {
  const z = useZero();
  const workflowId = app.backingWorkflowId ?? "";
  const { branchId: urlBranchId } = useCurrentUrl(urlRoutes.space.workflow);
  const [workflow] = useQuery(
    workflowId
      ? queries.workflowBranchRoutes({
          workflowId,
          ...(urlBranchId && { branchId: urlBranchId }),
        })
      : undefined
  );
  const canWrite = useHasAccess("space:write", workflow?.spaceId ?? "");
  const customDomain = useTenantCustomDomain();
  const externalPublicUrl = publicUrl();
  const domain = publicDomain();
  const spaceSlug = workflow?.space?.slug ?? "";

  const activeBranch = workflow?.branches;
  const head = activeBranch?.headCommit;

  const connectorStepDirs = new Set<string>();
  for (const f of head?.files ?? []) {
    const stepDir = stepDirFromPath(f.path);
    if (!stepDir) continue;
    const content = f.blob?.content;
    if (typeof content !== "string") continue;
    if (isConnectorRouteAuth(displayStepConfig(content).routeAuth)) {
      connectorStepDirs.add(stepDir);
    }
  }

  const entries: RouteEntry[] = [];
  for (const r of head?.routes ?? []) {
    if (!connectorStepDirs.has(r.stepDir)) continue;
    entries.push({
      branchId: activeBranch?.id ?? "",
      branchName: activeBranch?.name ?? "",
      stepDir: r.stepDir,
      route: r.route,
      routeType: isRouteType(r.routeType) ? r.routeType : "other",
    });
  }
  entries.sort((a, b) => a.route.localeCompare(b.route));

  const navigateOnBranchChange = useNavigateOnBranchChange(
    activeBranch?.id ?? "",
    workflow ? { spaceId: workflow.spaceId, workflowId } : undefined
  );

  const askAgent = () => {
    if (!activeBranch || !workflow) return;
    closeModal();
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.sendMessage({
          ...branchParams,
          entryId: nanoid(12),
          text: SET_UP_ROUTES_PROMPT,
          actionKind: "connector",
          ...clientSendContext(),
        })
      )
    );
  };

  const askButton = canWrite ? (
    <SoftButton variant="secondary" onClick={askAgent}>
      Ask 3B to set up links
    </SoftButton>
  ) : null;

  const empty: {
    title: string;
    description: string | null;
    action: React.ReactNode;
  } | null = !workflowId
    ? {
        title: "No workflow selected",
        description: "Pick a workflow above to see its links.",
        action: null,
      }
    : entries.length === 0
      ? {
          title: "No links exposed yet",
          description: null,
          action: askButton,
        }
      : null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      {empty ? (
        <StepList>
          <EmptyRow>
            <IconBadge>
              <Icon name="product-cases" size={16} />
            </IconBadge>
            <StepInfoContainer>
              <Text variant="text-regular-md" color="var(--text-primary)">
                {empty.title}
              </Text>
              {empty.description && (
                <Text variant="text-small" color="var(--text-secondary)">
                  {empty.description}
                </Text>
              )}
            </StepInfoContainer>
            {empty.action}
          </EmptyRow>
        </StepList>
      ) : (
        <StepList>
          {entries.map((e) => {
            const fullUrl = getPageUrl(
              spaceSlug,
              e.route,
              externalPublicUrl,
              domain,
              { branchId: e.branchName === "main" ? null : e.branchId },
              customDomain
            );
            return (
              <RouteRow key={`${e.branchId}:${e.route}`}>
                <Tooltip content={routeTypeLabel(e.routeType)}>
                  <IconBadge>
                    <Icon name={ROUTE_TYPE_ICONS[e.routeType]} size={16} />
                  </IconBadge>
                </Tooltip>
                <StepInfoContainer>
                  <Text
                    variant="text-regular-md"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {e.stepDir}
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {e.route}
                  </Text>
                </StepInfoContainer>
                <CopyLinkButton text={fullUrl} />
              </RouteRow>
            );
          })}
        </StepList>
      )}
      {workflowId ? (
        <HelperRow>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
          {!empty && canWrite && (
            <AskLink onClick={askAgent}>Ask 3B to update links</AskLink>
          )}
        </HelperRow>
      ) : null}
    </div>
  );
}
